"use client"

import React, { useEffect, useMemo, useState, useTransition } from "react"
import { debounce } from "next/dist/server/utils"
import { useRouter } from "next/navigation"
import { loadProducts } from "@/actions/productActions"
import { deleteWishlistAction } from "@/actions/wishlistActions"
import { LoaderCircle, PlusIcon, SearchIcon } from "lucide-react"

import { AccountContext } from "@/lib/storefront/account/account-service"
import { WishListWithId } from "@/lib/storefront/account/wishlist-service"
import { OrderLineItemData } from "@/lib/storefront/order/order"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { cn } from "@/lib/utils"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/components"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/components/alert-dialog"
import { Button } from "@/components/ui/components/button"
import { AccountWishlistsMiniCard } from "@/components/account/wishlists/AccountWishlistsMiniCard"
import { AccountWishlistDetailNewForm } from "@/components/account/wishlists/detail/AccountWishlistDetailNewForm"
import { UbAccountWishlistsViewStoryblok } from "@/components/storyblok/component-types"

interface AccountWishlistsClientProps {
  accountContext: AccountContext
  wishlists: WishListWithId[] | undefined
  blok: UbAccountWishlistsViewStoryblok
}

function WishlistCard(props: {
  wishlist: WishListWithId
  onRemove: (id: string) => void
  isoLocale: string
  blok: UbAccountWishlistsViewStoryblok
}) {
  const [products, setProducts] = useState<ReducedProductData[]>([])
  const productSkus = useMemo(
    () =>
      (props.wishlist.items
        ?.map((item) => {
          const skuAttr = item.attributes?.find((attr) => attr.name === "sku")
          return skuAttr && skuAttr.value !== undefined ? String(skuAttr.value) : undefined
        })
        ?.filter(Boolean) as string[]) || [],
    [props.wishlist.items]
  )
  const lineItems: OrderLineItemData[] = useMemo(() => {
    return props.wishlist.items
      ?.map((item) => {
        const quantity = (item.attributes ?? []).find((a) => a.name == "desiredQuantity") as any | undefined
        const skuAttr = item.attributes?.find((attr) => attr.name === "sku")
        const productID = skuAttr && skuAttr.value !== undefined ? String(skuAttr.value) : undefined
        if (!productID) return false

        return {
          product: { id: productID },
          orderLineItem: {
            quantity: {
              value: quantity?.value?.value ?? 1,
            },
            product: productID,
          },
        }
      })
      .filter(Boolean) as OrderLineItemData[]
  }, [props.wishlist.items])
  const router = useRouter()

  useEffect(() => {
    if (productSkus.length > 0) {
      // stops fetching server on first render
      loadProducts(productSkus.slice(0, 8)).then((loadedProducts) =>
        setProducts(loadedProducts.filter((l) => l != undefined))
      )
    }
  }, [productSkus])

  return (
    <div
      onClick={() => router.push(`/account/wishlist/${props.wishlist.id}`)}
      className={
        "group/entry flex w-full cursor-pointer flex-col gap-1 rounded-sm border border-gray-200 bg-white p-2 hover:shadow"
      }
    >
      <AccountWishlistsMiniCard
        wishlist={props.wishlist}
        onRemove={props.onRemove}
        isoLocale={props.isoLocale}
        productSkus={productSkus}
        products={products}
        productItemsCountLabel={String(props.blok.label_wishlist_product_items_count)}
        showPDFLink={
          productSkus &&
          productSkus.length > 0 &&
          props.blok.link_pdf_download != undefined &&
          props.blok.link_pdf_download.length > 0
        }
        showReorderButton={lineItems && lineItems.length > 0}
        reorderButtonLabelBuyAgain={props.blok.label_button_buy_again}
        reorderButtonLabelIsAdding={props.blok.label_product_is_adding}
        pdfLinkUrl={props.blok.link_pdf_download}
        reorderButtonLineItems={lineItems}
      />
    </div>
  )
}

export default function AccountWishlistsClient(props: AccountWishlistsClientProps) {
  const [baseWishlists, setBaseWishlists] = useState(props.wishlists)
  const [currentWishlists, setCurrentWishlists] = useState<WishListWithId[] | undefined>()
  const [openCreateForm, setOpenCreateForm] = useState(false)
  const [selectedSort, setSelectedSort] = useState<"SORT_BY_DATE_ASC" | "SORT_BY_DATE_DESC">("SORT_BY_DATE_DESC")
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = debounce(filterWishlistsByTitle, 500)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [toBeDeleted, setToBeDeleted] = useState<string | null>(null)
  const [loadingDelete, deleteTransaction] = useTransition()

  function handleSelectSort(key: "SORT_BY_DATE_ASC" | "SORT_BY_DATE_DESC") {
    setSelectedSort(key)
    sortWishlist()
  }
  function onSearchTermChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearchTerm(e.target.value)
    debouncedSearch(e.target.value)
  }

  function filterWishlistsByTitle(key: string) {
    if (key == "") {
      setCurrentWishlists(baseWishlists)
    } else {
      const reg = new RegExp(key, "i")
      setCurrentWishlists(props.wishlists?.filter((v) => (v.title ?? "").match(reg)))
    }
    sortWishlist()
  }

  function sortWishlist() {
    setCurrentWishlists((w) =>
      w?.sort((a, b) => {
        if (a.creationDate == undefined) return -1
        if (b.creationDate == undefined) return +1

        switch (selectedSort) {
          case "SORT_BY_DATE_ASC":
            if (a.creationDate < b.creationDate) return -1
            if (a.creationDate > b.creationDate) return +1
            break
          case "SORT_BY_DATE_DESC":
            if (a.creationDate > b.creationDate) return -1
            if (a.creationDate < b.creationDate) return +1
            break
        }
        return 0
      })
    )
  }

  async function handleDelete(id: string) {
    setToBeDeleted(id)
    setConfirmDialogOpen(true)
  }
  async function resetDelete() {
    setConfirmDialogOpen(false)
    setToBeDeleted(null)
  }
  async function handleConfirmedDelete() {
    deleteTransaction(async () => {
      if (toBeDeleted) {
        await deleteWishlistAction(toBeDeleted)
        setCurrentWishlists(currentWishlists?.filter((w) => w.id !== toBeDeleted))
        setBaseWishlists(baseWishlists?.filter((w) => w.id !== toBeDeleted))
      }
      await resetDelete()
    })
  }

  useEffect(() => {
    setCurrentWishlists(baseWishlists)
    sortWishlist()
  }, [baseWishlists])

  return (
    <>
      <div className={"flex flex-col gap-4"}>
        <div className="rounded-sm border border-gray-300 bg-gray-200">
          <div className="grid grid-rows-1 gap-2 p-4 lg:grid-cols-[min-content,_auto,_max-content] lg:grid-rows-2">
            <div className={"lg:order-1"}>
              <Select onValueChange={handleSelectSort} value={selectedSort}>
                <SelectTrigger className="w-full rounded-lg">
                  <SelectValue placeholder="" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {["SORT_BY_DATE_ASC", "SORT_BY_DATE_DESC"].map((currentSort: any) => (
                      <SelectItem value={currentSort} key={currentSort} onClick={() => handleSelectSort(currentSort)}>
                        {currentSort == "SORT_BY_DATE_ASC"
                          ? props.blok.label_wishlist_by_date_sort_asc
                          : props.blok.label_wishlist_by_date_sort_desc}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className={"max-lg:hidden lg:order-2"} />

            <div className="relative h-fit w-full md:pr-0 lg:order-4 lg:w-80">
              {searchTerm != "" ? (
                <button
                  onClick={() => {
                    setSearchTerm("")
                    debouncedSearch("")
                  }}
                  className={"absolute right-2 top-1/2 size-5 -translate-y-1/2 text-gray-400"}
                >
                  <PlusIcon strokeWidth={2} className={"size-5 rotate-45"} />
                </button>
              ) : (
                <div className={"pointer-events-none absolute right-2 top-1/2 size-5 -translate-y-1/2 text-gray-400"}>
                  <SearchIcon strokeWidth={2} className="size-5" />
                </div>
              )}
              <input
                type="text"
                placeholder={props.blok?.label_placeholder_search_wishlists}
                value={searchTerm}
                onChange={onSearchTermChange}
                className="w-full rounded-lg border border-gray-300 py-2 pl-4 pr-10 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className={"max-lg:hidden lg:order-5"} />
            <div className={"flex lg:order-3"}>
              <p className="my-auto ml-auto text-sm text-gray-800">
                {(props.blok.label_substitute_wishlist_created_wishlists + "").replace(
                  "%",
                  String(currentWishlists?.length)
                )}
              </p>
            </div>
            <div className={"flex max-lg:justify-center lg:order-6"}>
              <Button
                size={"default"}
                variant={"outline"}
                className="bg-primary text-white max-lg:mx-auto lg:ml-auto"
                onClick={() => setOpenCreateForm(!openCreateForm)}
              >
                <PlusIcon strokeWidth={2} className={cn("inline", { "rotate-45": openCreateForm })} />
                <span className="ml-2">
                  {openCreateForm ? props.blok.common_cancel : props.blok.label_new_wishlist_form_create}
                </span>
              </Button>
            </div>
          </div>
          <div className={cn("w-full rounded-b-sm", { "h-0 overflow-hidden": !openCreateForm })}>
            <div
              className={
                "relative z-0 w-full rounded-b-sm border-t border-t-gray-300 before:absolute before:-top-2 before:right-[calc(50%_-_1rem)] before:z-0 before:block before:size-8 before:rotate-45 before:rounded-sm before:border before:border-gray-300 before:border-b-white before:border-r-white before:bg-white lg:before:right-24"
              }
            >
              <div className={"relative z-10 w-full rounded-b-sm bg-white p-4"}>
                <AccountWishlistDetailNewForm onCancelAction={() => setOpenCreateForm(false)} blok={props.blok} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid max-h-full grid-flow-row gap-2 lg:grid-cols-2">
          {currentWishlists?.map((wishlist) => (
            <WishlistCard
              key={wishlist.id}
              wishlist={wishlist}
              onRemove={handleDelete}
              isoLocale={props.accountContext.isoLocale}
              blok={props.blok}
            />
          ))}
        </div>
      </div>
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen} defaultOpen={false}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{props.blok.alert_title}</AlertDialogTitle>
            <AlertDialogDescription>{props.blok.alert_message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={resetDelete} disabled={loadingDelete}>
              {props.blok.common_cancel}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedDelete} disabled={loadingDelete}>
              {loadingDelete ? <LoaderCircle className={"motion-safe:animate-spin"} /> : props.blok.common_confirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
